import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  useLazyAuthLoginQuery,
  useLazyGetContractorVersionQuery,
  useLogOutMutation,
} from '@store/rtk/auth';
import {
  selectUsers,
  setIsContractorVersion,
  setUserInfo,
  setUserType,
} from '@store/slices/users/usersSlice';
import {
  getTabId,
  ModuleType,
  sessionSetter,
  updateXHeaderId,
} from '@utils/storage';
import { decrypt, getEncryptedHash } from '@src/utils';
import { LOGIN_PAGE_URL, X_HEADER_ID_NAME } from '@utils/constants';
import { AUTH_CHANNEL } from '@utils/authConfig';
import {
  isPasswordTokenPage,
  isPublicAuthPage,
  isTrackRoute,
  shouldSkipAuth,
} from '@utils/auth';
import { useLazyGetUserNotificationQuery } from '@store/rtk/users';
import { useSelector } from 'react-redux';
import { selectNotification } from '@store/slices/notification/notification';
import { STORAGE_VERSION_KEY } from '@page/ConfigWrapper';
import { CONTRACTOR_USER, WEB_USER } from '@routes/helpers';

const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, isNotificationRead } = useAppSelector(selectUsers);
  const { hasFetchedSsoNotification } = useSelector(selectNotification);

  const [getUserInfo, { isFetching, isUninitialized }] =
    useLazyAuthLoginQuery();
  const [getUserNotification] = useLazyGetUserNotificationQuery();
  const [getContractorVersion] = useLazyGetContractorVersionQuery();
  const [logOut] = useLogOutMutation();

  const tabId = useMemo(() => getTabId(), []);
  const trackRoute = isTrackRoute(location.pathname);
  const publicAuthPage = isPublicAuthPage(location.pathname);

  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_CHANNEL);

    channel.onmessage = (event) => {
      const { type, from } = event.data || {};
      if (type === 'logout' && from !== tabId) {
        dispatch(setUserInfo(null));
        sessionSetter.clearAll();
        window.location.href = LOGIN_PAGE_URL;
      }
    };

    return () => channel.close();
  }, [dispatch, tabId]);

  useLayoutEffect(() => {
    const isPasswordToken = isPasswordTokenPage(location.pathname);

    if (isPasswordToken) {
      logOut({});
      const channel = new BroadcastChannel(AUTH_CHANNEL);
      channel.postMessage({ type: 'logout', from: tabId });
      channel.close();
    }

    if (userInfo && isPasswordToken) {
      dispatch(setUserInfo(null));
      sessionSetter.clearAll();
      sessionStorage.removeItem('tab_instance_id');
    }
  }, [location.pathname, userInfo, logOut, dispatch, tabId]);

  const fetchUserInfo = useCallback(async () => {
    const ssoInProgress = sessionStorage.getItem('sso_in_progress');

    if (
      trackRoute ||
      publicAuthPage ||
      isPasswordTokenPage(location.pathname) ||
      ssoInProgress
    )
      return;

    try {
      const result = await getUserInfo({}).unwrap();
      if (result) {
        if (result.userType === CONTRACTOR_USER) {
          updateXHeaderId('payor', null);
          updateXHeaderId('contractor', result.associated);
        } else {
          updateXHeaderId('contractor', null);
          updateXHeaderId('payor', result.associated);
        }

        updateXHeaderId('user', result.id);

        const fullPath = location?.state?.from || location.pathname;
        const [hasHash, hash] = getEncryptedHash(fullPath);

        if (hasHash && !location.pathname.includes('reporting')) {
          const resultDecrypt = decrypt({ byModuleId: hash });
          Object.keys(resultDecrypt).forEach((key) => {
            if (key !== 'user')
              updateXHeaderId(key as ModuleType, resultDecrypt[key]);
          });
        }

        if (result.associated && result.userType !== CONTRACTOR_USER) {
          try {
            const isContractor = await getContractorVersion({
              payerId: result.associated,
            }).unwrap();
            dispatch(setIsContractorVersion(Boolean(isContractor)));
          } catch {
            dispatch(setIsContractorVersion(false));
          }
        } else {
          dispatch(setIsContractorVersion(false));
        }

        dispatch(setUserInfo(result));
        dispatch(setUserType(result.userType ?? WEB_USER));
        localStorage.setItem(STORAGE_VERSION_KEY, result.userType ?? WEB_USER);

        if (location.pathname === LOGIN_PAGE_URL) {
          navigate(fullPath, { replace: true });
        }
      }
    } catch (error) {
      if (!publicAuthPage && !trackRoute) {
        dispatch(setUserInfo(null));
        sessionSetter.removeItem(X_HEADER_ID_NAME);
        navigate(LOGIN_PAGE_URL, { replace: true });
      }
    }
  }, [
    location.pathname,
    trackRoute,
    publicAuthPage,
    getUserInfo,
    getContractorVersion,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  useEffect(() => {
    // TODO check and remove after proper check
    // if (userInfo && !isNotificationRead && !hasFetchedSsoNotification) {
    if (userInfo && !userInfo.ssoNotificationDeleted) {
      getUserNotification({});
    }
    // TODO check and remove after proper check
    // }, [hasFetchedSsoNotification, userInfo]);
  }, [userInfo]);

  useEffect(() => {
    if (trackRoute || isFetching || isUninitialized) return;

    const authChecked: any = sessionSetter.getItem(X_HEADER_ID_NAME) || {};

    if (userInfo || shouldSkipAuth(location.pathname, location.search)) return;

    if (!authChecked?.user && !trackRoute) {
      navigate(LOGIN_PAGE_URL, {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [
    location.pathname,
    location.search,
    userInfo,
    isFetching,
    isUninitialized,
    isTrackRoute,
    navigate,
  ]);
};

export default useAuth;
